import type { LucideIcon } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";

import { cn } from "@krea/shared/utils";

export type SectionNavItem = {
  id: string;
  label: string;
  icon?: LucideIcon;
  children?: SectionNavItem[];
};

const REPAYMENT_CAPACITY_SECTIONS: SectionNavItem[] = [
  { id: "revenue-overview", label: "Revenue & risk" },
  { id: "financial-year", label: "Annual financials" },
  { id: "key-ratios", label: "Financial ratios" },
];

function scrollToSection(
  id: string,
  offsetTopPx: number,
  scrollContainerSelector?: string,
): void {
  const el = document.getElementById(id);
  if (!el) return;

  const scrollContainer = scrollContainerSelector
    ? document.querySelector<HTMLElement>(scrollContainerSelector)
    : null;

  if (scrollContainer) {
    const containerRect = scrollContainer.getBoundingClientRect();
    const elRect = el.getBoundingClientRect();
    const scrollTop =
      scrollContainer.scrollTop +
      (elRect.top - containerRect.top) -
      offsetTopPx -
      12;
    scrollContainer.scrollTo({ top: scrollTop, behavior: "smooth" });
  } else {
    const rect = el.getBoundingClientRect();
    const top = window.scrollY + rect.top - offsetTopPx - 12;
    window.scrollTo({ top, behavior: "smooth" });
  }

  window.history.replaceState(null, "", `#${id}`);
}

export function SectionNav({
  sections = REPAYMENT_CAPACITY_SECTIONS,
  scrollContainerSelector,
  offsetTopPx = 0,
}: {
  sections?: SectionNavItem[];
  scrollContainerSelector?: string;
  offsetTopPx?: number;
}) {
  const allIds = useMemo(() => {
    const ids: string[] = [];
    for (const s of sections) {
      ids.push(s.id);
      if (s.children) for (const c of s.children) ids.push(c.id);
    }
    return ids;
  }, [sections]);

  const allChildIds = useMemo(
    () => sections.flatMap((s) => s.children?.map((c) => c.id) ?? []),
    [sections],
  );

  const [activeId, setActiveId] = useState(sections[0]?.id ?? "");
  // Optimistic initial set — effect prunes to DOM-visible IDs after mount
  const [visibleChildIds, setVisibleChildIds] = useState<Set<string>>(
    () => new Set(allChildIds),
  );
  const clickLockRef = useRef<number | null>(null);

  useEffect(() => {
    setVisibleChildIds(
      new Set(allChildIds.filter((id) => Boolean(document.getElementById(id)))),
    );
  }, [allChildIds]);

  useEffect(() => {
    const els = allIds
      .map((id) => document.getElementById(id))
      .filter((el): el is HTMLElement => Boolean(el));
    if (els.length === 0) return;

    const scrollContainer = scrollContainerSelector
      ? document.querySelector<HTMLElement>(scrollContainerSelector)
      : null;

    const observer = new IntersectionObserver(
      (entries) => {
        if (clickLockRef.current) return;
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]?.target?.id) {
          setActiveId(visible[0].target.id);
          window.history.replaceState(null, "", `#${visible[0].target.id}`);
        }
      },
      {
        root: scrollContainer,
        threshold: 0,
        rootMargin: `-${offsetTopPx + 8}px 0px -55% 0px`,
      },
    );

    for (const el of els) observer.observe(el);
    return () => observer.disconnect();
  }, [allIds, offsetTopPx, scrollContainerSelector]);

  useEffect(() => {
    return () => {
      if (clickLockRef.current) {
        clearTimeout(clickLockRef.current);
        clickLockRef.current = null;
      }
    };
  }, []);

  const onNavigate = (id: string): void => {
    setActiveId(id);
    clickLockRef.current = window.setTimeout(() => {
      clickLockRef.current = null;
    }, 700);
    scrollToSection(id, offsetTopPx, scrollContainerSelector);
  };

  return (
    <nav
      aria-label="Section navigation"
      className="sticky space-y-0.5"
      style={{ top: `${offsetTopPx}px` }}
    >
      {sections.map((item) => {
        const Icon = item.icon;
        const hasActiveChild =
          item.children?.some((c) => c.id === activeId) ?? false;
        const isActive = item.id === activeId || hasActiveChild;
        const showChildren = item.children?.some((c) =>
          visibleChildIds.has(c.id),
        );

        return (
          <div key={item.id}>
            <button
              type="button"
              onClick={() => onNavigate(item.id)}
              className={cn(
                "flex w-full cursor-pointer items-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition-colors",
                isActive
                  ? "bg-primary-150 text-primary"
                  : "text-primary-400 hover:bg-primary-150 hover:text-primary",
              )}
            >
              {Icon ? <Icon className="h-4 w-4 shrink-0" /> : null}
              <span>{item.label}</span>
            </button>

            {showChildren && (
              <div className="ml-3 mt-0.5 space-y-0.5 border-l border-border pl-2">
                {(item.children ?? [])
                  .filter((child) => visibleChildIds.has(child.id))
                  .map((child) => {
                    const isChildActive = child.id === activeId;
                    return (
                      <button
                        key={child.id}
                        type="button"
                        onClick={() => onNavigate(child.id)}
                        className={cn(
                          "flex w-full cursor-pointer items-center rounded-md px-3 py-1.5 text-xs font-medium transition-colors",
                          isChildActive
                            ? "bg-primary-150 text-primary"
                            : "text-primary-400 hover:bg-primary-150 hover:text-primary",
                        )}
                      >
                        {child.label}
                      </button>
                    );
                  })}
              </div>
            )}
          </div>
        );
      })}
    </nav>
  );
}
